import { useEffect, useCallback } from "react";
import Conversation, { type ReviewStatus } from "admin/types/interfaces/conversation";
import { useMutator } from "~/hooks/useFetcher";

const { VITE_ADMIN_CONVERSATIONS_API, VITE_CONVERSATIONS_API } = import.meta.env;

export type BinaryFeedbackFilter = "POSITIVE" | "NEGATIVE";
export type ImpactFilter = "SPECIFIED" | "NONE" | "POSITIVE" | "NEGATIVE";
export type ConversationVisibility = "OWN_ONLY" | "FEEDBACK_AND_OWN" | "ALL";

interface ConversationSearchRequest {
  user_id?: string;
  agent_id?: string;
  app?: string;
  resource_id?: string;
  dataset_name?: string;
  binary_feedback_filter?: BinaryFeedbackFilter[];
  has_annotations?: boolean;
  has_comments?: boolean;
  impact_filter?: ImpactFilter[];
  review_status?: ReviewStatus[];
}

interface PageMetadata {
  limit: number;
  offset: number;
  totalCount: number;
  hasMore: boolean;
}

interface ConversationListResponse {
  items: Conversation[];
  metadata: PageMetadata;
  visibility?: ConversationVisibility;
}

export type SortKey =
  | "updated_at"
  | "created_at"
  | "number_of_annotations"
  | "number_of_turns"
  | "number_of_positive_feedback"
  | "number_of_negative_feedback"
  | "number_of_comments";
export type SortOrder = "asc" | "desc";

interface UseConversationsParams {
  limit?: number;
  offset?: number;
  userId?: string;
  agentId?: string;
  appId?: string;
  resourceId?: string;
  datasetName?: string;
  feedbackFilter?: BinaryFeedbackFilter[];
  hasAnnotations?: boolean | null;
  hasComments?: boolean | null;
  impactFilter?: ImpactFilter[];
  reviewStatus?: ReviewStatus[];
  sortKey?: SortKey;
  sortOrder?: SortOrder;
  // Restricts results to project conversations whose project exists and isn't
  // soft-deleted. Only honored by the self-scoped endpoint (isAdmin: false) —
  // the admin cross-user search doesn't expose this param, so it's a no-op there.
  nextgenOnly?: boolean;
  // When true, query the admin cross-user search; otherwise query the caller's
  // own conversations. Admin-only filters are ignored by the self-scoped endpoint.
  isAdmin?: boolean;
  // Gate the fetch until the caller knows which endpoint to hit. `isAdmin` is
  // derived from permissions that load async (false until /me resolves), so
  // firing early would query the self endpoint and flash OWN_ONLY for admins.
  enabled?: boolean;
}

/**
 * Custom hook for fetching conversations with pagination and filtering.
 *
 * Filters with OR semantics (binary_feedback_filter, impact_filter, review_status)
 * accept arrays; empty arrays are treated as "no filter" and dropped from the request.
 */
const useConversations = ({
  limit,
  offset = 0,
  userId,
  agentId,
  appId,
  resourceId,
  datasetName,
  feedbackFilter,
  hasAnnotations,
  hasComments,
  impactFilter,
  reviewStatus,
  sortKey = "updated_at",
  sortOrder = "desc",
  nextgenOnly = false,
  isAdmin = true,
  enabled = true,
}: UseConversationsParams = {}) => {
  const buildUrl = () => {
    const params = new URLSearchParams();

    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }
    if (offset !== undefined) {
      params.append("offset", offset.toString());
    }
    if (sortKey) {
      params.append("sort_key", sortKey);
    }
    if (sortOrder) {
      params.append("sort_order", sortOrder);
    }
    if (nextgenOnly && !isAdmin) {
      params.append("nextgen_only", "true");
    }

    const queryString = params.toString();
    const baseUrl = isAdmin ? VITE_ADMIN_CONVERSATIONS_API : VITE_CONVERSATIONS_API;
    return `${baseUrl}/search${queryString ? `?${queryString}` : ""}`;
  };

  const currentUrl = buildUrl();

  const {
    data,
    error,
    isMutating: isLoading,
    trigger,
  } = useMutator<ConversationListResponse, ConversationSearchRequest>(currentUrl, {
    requestInit: {
      method: "POST",
    },
    shouldCamelize: true,
  });

  const search = useCallback(
    async (searchRequest: ConversationSearchRequest = {}) => {
      return await trigger(searchRequest);
    },
    [trigger]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const searchRequest: ConversationSearchRequest = {};
    if (userId) {
      searchRequest.user_id = userId;
    }
    if (agentId) {
      searchRequest.agent_id = agentId;
    }
    if (appId) {
      searchRequest.app = appId;
    }
    if (resourceId) {
      searchRequest.resource_id = resourceId;
    }
    if (datasetName) {
      searchRequest.dataset_name = datasetName;
    }
    if (feedbackFilter?.length) {
      searchRequest.binary_feedback_filter = feedbackFilter;
    }
    if (hasAnnotations !== undefined && hasAnnotations !== null) {
      searchRequest.has_annotations = hasAnnotations;
    }
    if (hasComments !== undefined && hasComments !== null) {
      searchRequest.has_comments = hasComments;
    }
    if (impactFilter?.length) {
      searchRequest.impact_filter = impactFilter;
    }
    if (reviewStatus?.length) {
      searchRequest.review_status = reviewStatus;
    }
    search(searchRequest);
  }, [
    limit,
    offset,
    userId,
    agentId,
    appId,
    resourceId,
    datasetName,
    feedbackFilter,
    hasAnnotations,
    hasComments,
    impactFilter,
    reviewStatus,
    sortKey,
    sortOrder,
    // isAdmin and nextgenOnly both affect buildUrl's output, so re-run when
    // either flips (e.g. isAdmin after /me loads) to refetch from the correct
    // URL instead of serving a stale response cached under the old one.
    nextgenOnly,
    isAdmin,
    enabled,
    search,
  ]);

  return {
    data: data?.items || [],
    total: data?.metadata?.totalCount,
    limit: data?.metadata?.limit,
    offset: data?.metadata?.offset,
    visibility: data?.visibility,
    error,
    isLoading,
    isValidating: isLoading,
    mutate: search,
  };
};

export default useConversations;
